import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { OnlineSalesClosedNotice } from './OnlineSalesClosedNotice.tsx';
import { TicketGroupsList } from './TicketGroupsList.tsx';
import { themeOf } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { ticketShopHref } from '../../lib/outbound.ts';
import { resolveOnlineSales } from '../../domain/onlineSales.ts';
import { groupTicketsForDisplay } from '../../domain/ticketGroups.ts';
import type { TicketsSection } from '../../lib/types.ts';

/**
 * Tickets section (CMS-039): shows the editor-selected ticket categories
 * (Adgangsbilletter/Glamping/Overnatning), reusing `groupTicketsForDisplay`. When no categories
 * are selected it shows all groups — absorbing the former standalone `allTickets` section, which
 * always showed everything.
 *
 * Sets id="billetter" so the hardcoded "Køb billet" nav pill (TopNav) keeps landing here,
 * same as the pre-CMS-038 home page (CMS-038).
 *
 * While online sales are closed (between this year's sale closing and next year's opening — the
 * section's or tickets.json's `onlineSales` block says so) the list and its buy buttons give way
 * to the publisher's "online sales have stopped" message.
 */
export function CmsTicketsSection({ theme, guitar, categories, onlineSales }: TicketsSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();

  const selected = new Set(categories ?? []);
  const allGroups = data ? groupTicketsForDisplay(data.tickets) : [];
  // No selection = show all groups (the old "Alle billetter" behaviour); otherwise only the chosen.
  const groups = selected.size === 0 ? allGroups : allGroups.filter(g => selected.has(g.group));
  const hasTickets = groups.length > 0;
  const sales = resolveOnlineSales(onlineSales, data?.tickets.onlineSales);
  // The listed tickets come from tickets.json, which follows the *ticket* year (the sale windows)
  // — from the evening next year's blind bird opens that is already next year, while program.json
  // still describes the year just finished until its teaser. So the buy button must use the
  // storefront published alongside those tickets; program.json's ticketsUrl is the fallback for
  // older feeds that don't carry it yet, and the on-page anchor when neither is set.
  // …and in production it goes out through /ud/billetter, so the click is counted server-side
  // without a tracking script (see lib/outbound.ts).
  const buyUrl = ticketShopHref(data?.tickets.onlineSales?.url ?? data?.program.tickets?.ticketsUrl);

  // The section's own onlineSales block is baked into the page JSON, so the closed notice can be
  // shown even when tickets.json/program.json failed to load — a visitor never sees a technical
  // error here.
  if (!loading && !sales.open) {
    return (
      <CmsSectionShell theme={theme} guitar={guitar} width="5xl" id="billetter">
        <OnlineSalesClosedNotice message={sales.message ?? ''} theme={t} />
      </CmsSectionShell>
    );
  }

  return (
    <CmsSectionShell theme={theme} guitar={guitar} width="5xl" id="billetter">
      <CmsSectionState
        loading={loading}
        error={!!error}
        empty={!hasTickets}
        loadingLabel="Indlæser billetter…"
        bodyClass={t.body}
        errorMessage="Billetterne vises snart."
        emptyMessage="Der er ingen billetter til salg lige nu."
      >
        <TicketGroupsList groups={groups} theme={t} buyUrl={buyUrl} />
      </CmsSectionState>
    </CmsSectionShell>
  );
}
